/*=========================================================================================
  File Name: actions.js
  Namespace: courses
  Description: Vuex Store - Course actions
==========================================================================================*/

import {
  createFile,
  deleteFile,
  editFile,
  getCourse,
  getCoursesWithoutAdapter,
  listBusinessAccounts,
  listCourses,
  uploadFile,
} from '@/services/courses.service'
import {
  buildEmptyEnterpriseAccountsResponse,
  saveCourseWithCustomContentFlow,
} from '@/services/contentManager.service'

import { adaptCourseToStore } from '@/services/adapters/courseAdapter.js'
import { listFiles } from '@/services/files.services.js'
import { listRelatedContent } from '@/services/related.service.js'
import {
  ADD_FILE_CURRENT_COURSE,
  ADD_GROUP_TO_STRUCTURE,
  ADD_LEAFS_INTO_STRUCTURE,
  ADD_LEAF_TO_STRUCTURE,
  REMOVE_FILE_CURRENT_COURSE,
  REMOVE_GROUP_OF_STRUCTURE,
  REMOVE_LEAF_TO_STRUCTURE,
  RENAME_GROUP,
  RESET_CURRENT_COURSE,
  RESET_CURRENT_COURSE_CONTENT,
  STORE_CURRENT_COURSE,
  STORE_CURRENT_COURSE_FILES,
  STORE_LIST_COURSES,
  UPDATE_CONTENT_ORDER,
} from './types'

const actions = {
  async getCourseAction({ commit }, { courseId }) {
    const response = await getCourse({
      courseId,
    })

    if (response) {
      commit(STORE_CURRENT_COURSE, response)
    }
    return response
  },
  async getAllCourses({ commit, rootState }, params = {}) {
    params.company = rootState.app.company
    const response = await listCourses(params)
    if (response) {
      commit(STORE_LIST_COURSES, response)
    }
    return response
  },
  async saveCourseToApi(
    { commit, rootState, rootGetters, getters },
    { previousCourse = null } = {}
  ) {
    try {
      const response = await saveCourseWithCustomContentFlow({
        course: getters.getCurrentCourse,
        previousCourse: previousCourse || getters.getCurrentCourse,
        company: rootState.app.company,
        files: getters.getCurrentCourseFiles.map((f) => f.id),
        customContentCoursesEnabled:
          rootGetters['featureFlags/customContentCoursesEnabled'],
      })

      commit(STORE_CURRENT_COURSE, response)
      return response
    } catch (error) {
      if (error && error.context && error.context.course) {
        commit(STORE_CURRENT_COURSE, error.context.course)
      }
      throw error
    }
  },
  async uploadCourseCSV({ state, rootState }, formData) {
    const response = await uploadFile({
      file: formData,
      company: rootState.app.company,
      courseId: state.currentCourse.id,
    })
    return response
  },
  saveCourseToStore({ commit }, payload) {
    commit(STORE_CURRENT_COURSE, payload)
  },
  saveCourseToEdit({ commit }, course) {
    commit(
      STORE_CURRENT_COURSE,
      adaptCourseToStore({
        data: course,
      })
    )
  },
  addLeafToContentStructure({ commit }, leaf) {
    commit(ADD_LEAF_TO_STRUCTURE, leaf)
  },
  addLeafsBetweenContentStructure({ commit }, payload) {
    commit(ADD_LEAFS_INTO_STRUCTURE, payload)
  },
  removeLeafToContentStructure({ commit }, leaf) {
    commit(REMOVE_LEAF_TO_STRUCTURE, leaf)
  },
  resetCurrentCourse({ commit }) {
    commit(RESET_CURRENT_COURSE)
  },
  renameGroupOfContentStructure({ commit }, nameObject) {
    commit(RENAME_GROUP, nameObject)
  },
  addGroupToContentStructure({ commit }, newGroup) {
    commit(ADD_GROUP_TO_STRUCTURE, newGroup)
  },
  removeGroupToContentStructure({ commit }, group) {
    commit(REMOVE_GROUP_OF_STRUCTURE, group)
  },
  updateContentStructure({ commit }, contentToOrder) {
    commit(UPDATE_CONTENT_ORDER, contentToOrder)
  },
  deleteContentStructure({ commit }) {
    commit(RESET_CURRENT_COURSE_CONTENT)
  },
  async getAllCourseFiles({ commit }, files) {
    const response = await listFiles({
      ids: files,
    })
    commit(STORE_CURRENT_COURSE_FILES, response.results)
  },

  //// Files ////
  async createCourseFile({ commit, getters }, file) {
    const response = await createFile({
      file: file,
      courseId: getters.getCurrentCourse.id,
    })
    commit(ADD_FILE_CURRENT_COURSE, response)
  },
  async editCourseFile({ commit, getters }, file) {
    const response = await editFile({
      file: file,
      courseId: getters.getCurrentCourse.id,
    })
    commit(ADD_FILE_CURRENT_COURSE, response)
  },
  async deleteCourseFile({ commit, getters }, file) {
    await deleteFile({
      file: file,
      courseId: getters.getCurrentCourse.id,
    })
    commit(REMOVE_FILE_CURRENT_COURSE, file.id)
  },
  async searchCourses({ rootState }, params = {}) {
    params.company = rootState.app.company
    const response = await listCourses(params)
    return response
  },
  async searchRelatedContent(context, params = {}) {
    const response = await listRelatedContent(params)
    return response
  },
  async searchCoursesWithoutAdapter({ rootState }, params = {}) {
    params.company = rootState.app.company
    const response = await getCoursesWithoutAdapter(params)
    return response
  },
  async searchBusinessAccounts({ rootGetters }, params = {}) {
    if (!rootGetters['featureFlags/customContentCoursesEnabled']) {
      return buildEmptyEnterpriseAccountsResponse()
    }
    return listBusinessAccounts(params)
  },
}

export default actions
